import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

const PRODUCT_SECTION_IDS_KEY = 'quick-add-product-section-ids';

let latestQuickAddOpenId = 0;

/** @returns {Record<string, string>} */
function readProductSectionIds() {
  try {
    const stored = JSON.parse(sessionStorage.getItem(PRODUCT_SECTION_IDS_KEY) || '{}');
    const themeId = String(window.Shopify?.theme?.id ?? '');
    return stored.themeId === themeId && stored.ids ? stored.ids : {};
  } catch {
    return {};
  }
}

/**
 * @param {string} templateKey
 * @returns {string | undefined}
 */
function readProductSectionId(templateKey) {
  return readProductSectionIds()[templateKey];
}

/**
 * @param {string} templateKey
 * @param {string} sectionId
 */
function writeProductSectionId(templateKey, sectionId) {
  try {
    const ids = { ...readProductSectionIds(), [templateKey]: sectionId };
    sessionStorage.setItem(
      PRODUCT_SECTION_IDS_KEY,
      JSON.stringify({ themeId: String(window.Shopify?.theme?.id ?? ''), ids })
    );
  } catch {
    // Storage can be unavailable (private mode, quota); quick add then keeps using the full page.
  }
}

export class QuickAddComponent extends Component {
  /** @type {AbortController} */
  #abortController = new AbortController();
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, Promise<Element | null>>} */
  #pendingContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  /** @param {VariantSelectedEvent} event */
  #onVariantSelected = (event) => {
    this.#updateQuickAddButtonState(event);
  };

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    this.#cartUpdateAbortController.abort();
    this.#cartUpdateAbortController = new AbortController();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#onVariantSelected);
    this.#cachedContent.clear();
    this.#pendingContent.clear();
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
    this.#pendingContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    // Every card shares one dialog, so only the latest open may fill or close it.
    const openId = ++latestQuickAddOpenId;
    const isLatestOpen = () => openId === latestQuickAddOpenId;

    const button = event.target instanceof Element ? event.target.closest('button') : null;
    button?.setAttribute('aria-busy', 'true');
    this.#showQuickAddLoading();
    this.#openQuickAddModal();

    try {
      const productGrid = await this.#loadProductGrid(this.productPageUrl);
      if (!isLatestOpen()) return;

      if (productGrid) {
        // Use a fresh clone from the cache
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        const updated = await this.updateQuickAddModal(freshContent, isLatestOpen);
        if (updated) this.#updateVariantPicker(productGrid);
      } else {
        this.#closeQuickAddModal();
      }
    } catch (error) {
      if (isLatestOpen()) this.#closeQuickAddModal();
      throw error;
    } finally {
      button?.removeAttribute('aria-busy');
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #showQuickAddLoading() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    modalContent.replaceChildren();
    const status = document.createElement('p');
    status.className = 'quick-add-modal__loading';
    status.textContent = 'Loading options';
    modalContent.append(status);
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Starts loading the modal content early (hover, focus, card variant change) so the click can reuse it.
   * @param {string} productPageUrl - The URL of the product page to load
   */
  prefetchProductPage(productPageUrl) {
    this.#loadProductGrid(productPageUrl).catch(() => {});
  }

  /**
   * Returns the product grid for a URL, sharing one request between prefetch and click.
   * @param {string} productPageUrl - The URL of the product page to load
   * @returns {Promise<Element | null>}
   */
  #loadProductGrid(productPageUrl) {
    if (!productPageUrl) return Promise.resolve(null);

    const cached = this.#cachedContent.get(productPageUrl);
    if (cached) return Promise.resolve(cached);

    const pending = this.#pendingContent.get(productPageUrl);
    if (pending) return pending;

    const request = this.#requestProductGrid(productPageUrl)
      .then((productGrid) => {
        // A cart update while loading means this content may be stale, so don't cache it.
        if (productGrid && this.#pendingContent.get(productPageUrl) === request) {
          this.#cachedContent.set(productPageUrl, productGrid);
        }
        return productGrid;
      })
      .finally(() => {
        if (this.#pendingContent.get(productPageUrl) === request) this.#pendingContent.delete(productPageUrl);
      });

    this.#pendingContent.set(productPageUrl, request);
    return request;
  }

  /**
   * Renders only the product section when its id is known for this product template; otherwise
   * renders the full page and remembers the section id for next time.
   * @param {string} productPageUrl - The URL of the product page to load
   * @returns {Promise<Element | null>}
   */
  async #requestProductGrid(productPageUrl) {
    const templateKey = this.#templateKey;
    const sectionId = readProductSectionId(templateKey);

    if (sectionId) {
      const sectionUrl = new URL(productPageUrl);
      sectionUrl.searchParams.set('section_id', sectionId);
      const sectionHtml = await this.#fetchDocument(sectionUrl.toString());
      const sectionGrid = sectionHtml?.querySelector('[data-product-grid-content]');
      if (sectionGrid) return sectionGrid;
    }

    const html = await this.#fetchDocument(productPageUrl);
    const productGrid = html?.querySelector('[data-product-grid-content]');
    if (!productGrid) return null;

    const section = productGrid.closest('[id^="shopify-section-"]');
    // Cards built in the browser don't know their template, so only learn from cards that do.
    if (section && this.dataset.templateSuffix !== undefined) {
      writeProductSectionId(templateKey, section.id.replace('shopify-section-', ''));
    }

    return productGrid;
  }

  get #templateKey() {
    return this.dataset.templateSuffix || 'default';
  }

  /**
   * @param {string} url
   * @returns {Promise<Document | null>}
   */
  async #fetchDocument(url) {
    try {
      const response = await fetch(url, { signal: this.#abortController.signal });
      if (!response.ok) return null;

      return new DOMParser().parseFromString(await response.text(), 'text/html');
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return null;
      throw error;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   * @param {() => boolean} [isLatestOpen] - False once another card has opened the dialog
   * @returns {Promise<boolean>} Whether the dialog was updated
   */
  async updateQuickAddModal(productGrid, isLatestOpen = () => true) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return false;

    // Shop Pay and the other dynamic checkout buttons boot the full checkout app.
    // Inside this modal that download freezes the tab, and the cart already has checkout.
    productGrid.querySelectorAll('.accelerated-checkout-block').forEach((node) => node.remove());

    // Define the content's components before inserting it. Otherwise an undefined component's refs
    // (the charms panel has its own ref="dialog") are claimed by this dialog and break close.
    await Promise.all(
      Array.from(productGrid.querySelectorAll('script[type="module"][src]'), (script) =>
        import(/** @type {HTMLScriptElement} */ (script).src).catch(() => {})
      )
    );
    if (!isLatestOpen()) return false;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    return true;
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
